import { dist2, idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { hexToWorld } from '../hex.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { touchGrid } from '../grid.ts';

/**
 * The Drinker (El pozo 2, docs/SAGAS.md): a leech of ink the size of a cart, at the vein that feeds Musgoviejo's well,
 * the last of the descent's rooms. Its rule: hit it when it is swollen. Empty, it is quick and spits ink; then it goes
 * back to the vein and drinks, and swollen it is slow and takes double. When it bursts, the water comes back all at
 * once: after a roar in the dry pipes, a line of water rises through the rooms behind the heroes, the way they came
 * down. In it they wade at half their pace, and whoever stays in it drowns. Everyone standing back in the first room
 * wins it.
 */

const EMPTY = 0;
const DRINK = 1;
const FULL = 2;

/** The leech's own round: empty (spits and chases), back to the vein to drink, swollen. */
export const drinkerTick = (s: BattleState, e: Enemy, movePx: (e: Enemy) => number, chase: (px: (e: Enemy) => number) => void): void => {
  const r = s.rules?.drinker;
  const room = s.map.dungeon?.rooms[s.map.dungeon.rooms.length - 1];
  if (!r || !room) return;
  const vein = hexToWorld(room.q, room.r);
  if (e.timer > 0) e.timer--;
  if (e.phase === DRINK) {
    if (!withinRange(e.x, e.y, vein.x, vein.y, 300)) {
      e.anim = 'walk';
      stepToward(e, vein.x, vein.y, mulBp(movePx(e), r.fastBp));
      e.timer = r.drink;
      return;
    }
    e.anim = 'idle';
    if (e.timer > 0) return;
    e.phase = FULL;
    e.timer = r.full;
    ev(s, { t: 'drinker', kind: 'swell', x: e.x, y: e.y });
    return;
  }
  if (e.phase === FULL) {
    chase((x) => mulBp(movePx(x), r.slowBp));
    if (e.timer > 0) return;
    e.phase = EMPTY;
    e.timer = r.empty;
    ev(s, { t: 'drinker', kind: 'empty', x: e.x, y: e.y });
    return;
  }
  // Empty: it spits ink at whoever is nearest within reach, and runs after them.
  if (e.cd === 0) {
    let h: Hero | null = null;
    let bd = r.spit.range * r.spit.range;
    for (const o of s.heroes) {
      if (o.downTicks > 0) continue;
      const d = dist2(e.x, e.y, o.x, o.y);
      if (d > bd) continue;
      bd = d;
      h = o;
    }
    if (h && bd > 1500 * 1500) {
      e.cd = r.spit.every;
      e.anim = 'attack';
      e.animT = 0;
      damageHero(s, h, mulBp(e.damage, r.spit.dmgBp), e);
      ev(s, { t: 'beam', x: e.x, y: e.y, tx: h.x, ty: h.y, kind: 'inkSpit' });
      return;
    }
  }
  chase((x) => mulBp(movePx(x), r.fastBp));
  if (e.timer > 0) return;
  e.phase = DRINK;
  e.timer = r.drink;
  ev(s, { t: 'drinker', kind: 'drink', x: e.x, y: e.y });
};

/** What a blow on it lands as: double while it is swollen. */
export const drinkerTakenBp = (s: BattleState, e: Enemy): number => (e.phase === FULL ? (s.rules?.drinker?.fullBp ?? 10000) : 10000);

/**
 * The way the water comes back: from the vein, room by room, to the first room. Among the dragon's bones (La Orilla 4,
 * `up`) the other way: the sea comes in where they came down, and the way out is the last room, the rock.
 */
const chain = (s: BattleState): { x: number; y: number }[] => {
  const rooms = s.map.dungeon?.rooms ?? [];
  return (s.rules?.drinker?.up ? rooms.slice() : rooms.slice().reverse()).map((x) => hexToWorld(x.q, x.r));
};

/** The room everyone has to get to, out of the water. */
const outRoom = (s: BattleState) => {
  const rooms = s.map.dungeon?.rooms ?? [];
  return s.rules?.drinker?.up ? rooms[rooms.length - 1] : rooms[0];
};

/** How far up the way a point is (from the vein, along the rooms' chain). */
const along = (s: BattleState, x: number, y: number): number => {
  const pts = chain(s);
  let best = 0x7fffffff;
  let at = 0;
  let run = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1] as { x: number; y: number };
    const b = pts[i] as { x: number; y: number };
    const len = imax(1, isqrt(dist2(a.x, a.y, b.x, b.y)));
    // The point's projection on the segment, clamped to it.
    let t = idiv(((x - a.x) * (b.x - a.x) + (y - a.y) * (b.y - a.y)), len);
    if (t < 0) t = 0;
    if (t > len) t = len;
    const px = a.x + idiv((b.x - a.x) * t, len);
    const py = a.y + idiv((b.y - a.y) * t, len);
    const d = dist2(x, y, px, py);
    if (d < best) {
      best = d;
      at = run + t;
    }
    run += len;
  }
  return at;
};

/** Whether the water has got to a point yet. */
export const drinkerWet = (s: BattleState, x: number, y: number): boolean => !!s.drink?.burst && s.drink.front > 0 && along(s, x, y) <= s.drink.front;

/** The burst and the water coming back: wading, drowning, and everyone standing in the first room. */
export const drinkTick = (s: BattleState, clear: () => void): void => {
  const r = s.rules?.drinker;
  const d = s.dungeon;
  if (!r || !d) return;
  const st = (s.drink ??= { burst: 0, front: 0, out: 0 });
  if (st.out) return;
  if (!st.burst) {
    // La Orilla 4: the water is coming from the start, every room already behind them.
    if (r.up) {
      if (!s.started) return;
      st.burst = s.tick;
      d.cleared = d.cleared.map(() => true);
      // Every door open behind and ahead of them.
      touchGrid(s);
      if (r.heart) st.heart = { at: 'carried', pid: s.heroes.slice().sort((a, b) => a.id - b.id)[0]?.pid ?? '', x: 0, y: 0, soaked: 0 };
      ev(s, { t: 'drinker', kind: 'burst', x: 0, y: 0 });
      return;
    }
    if (!d.boss || s.enemies.some((e) => e.id === d.boss && e.hp > 0)) return;
    // It bursts: the doors open (the hall is done) and the pipes roar.
    st.burst = s.tick;
    clear();
    for (const e of s.enemies) if (e.hp > 0) e.hp = 0;
    ev(s, { t: 'drinker', kind: 'burst', x: 0, y: 0 });
    return;
  }
  if (s.tick < st.burst + r.flood.delay) return;
  if (st.front === 0) ev(s, { t: 'drinker', kind: 'wave', x: 0, y: 0 });
  st.front += r.flood.speed;
  for (const h of s.heroes) {
    if (h.downTicks > 0 || !drinkerWet(s, h.x, h.y)) continue;
    if ((s.tick - st.burst) % 12 === 0) damageHero(s, h, imax(1, mulBp(h.stats.maxHp, r.flood.drownBp)), null);
  }
  // The Drowned climb out at the water's edge, behind them.
  const dr = r.drowned;
  // Not before the water is a room's length in: the first ones would be at their heels before they could start.
  if (dr && (s.tick - st.burst) % dr.every === 0 && st.front > dr.after) {
    const at = pointAt(s, imax(0, st.front - 600));
    if (at) spawnEnemy(s, 'drowned', 1, false, 0, at.x, at.y, mulBp(dr.hp, s.rules?.enemyHpBp ?? 10000));
  }
  const L = s.map.dungeon;
  const entry = outRoom(s);
  if (!entry || !L) return;
  const c = hexToWorld(entry.q, entry.r);
  const rr = entry.radius * 1000;
  // The dragon's heart: carried (it slows its carrier), dropped where its carrier fell, lifted by whoever stands by it;
  // lying in the water a moment, the sea takes it, and that loses it.
  const heart = st.heart;
  const hk = r.heart;
  if (heart && hk) {
    const by = heart.at === 'carried' ? s.heroes.find((x) => x.pid === heart.pid) : undefined;
    if (heart.at === 'carried' && (!by || by.downTicks > 0)) {
      heart.at = 'ground';
      heart.pid = '';
    }
    if (heart.at === 'carried' && by) {
      heart.x = by.x;
      heart.y = by.y;
    } else if (heart.at === 'ground') {
      heart.soaked = drinkerWet(s, heart.x, heart.y) ? heart.soaked + 1 : 0;
      if (heart.soaked >= hk.soak) {
        heart.at = 'lost';
        ev(s, { t: 'drinker', kind: 'lost', x: heart.x, y: heart.y });
        return;
      }
      const lift = s.heroes.find((x) => x.downTicks === 0 && withinRange(x.x, x.y, heart.x, heart.y, hk.reach));
      if (lift) {
        heart.at = 'carried';
        heart.pid = lift.pid;
      }
    }
  }
  const up = s.heroes.filter((h) => h.downTicks === 0);
  const home = !heart || (heart.at === 'carried' && withinRange(heart.x, heart.y, c.x, c.y, rr));
  if (up.length && home && up.every((h) => withinRange(h.x, h.y, c.x, c.y, rr))) {
    st.out = s.tick;
    ev(s, { t: 'drinker', kind: 'out', x: c.x, y: c.y });
  }
};

/** Out with the water behind: won (a moment later); the heart lost to the sea: lost; everyone down: lost (the crypt's own rule). */
export const drinkResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.drinker;
  if (!r) return null;
  if (s.drink?.heart?.at === 'lost') return 'lost';
  return s.drink?.out && s.tick - s.drink.out >= r.beat ? 'won' : null;
};

/** The heroes' pace in the water (and with the dragon's heart in their arms). */
export const wadeBp = (s: BattleState, h: Hero): number => {
  const r = s.rules?.drinker;
  if (!r) return 10000;
  const bp = drinkerWet(s, h.x, h.y) ? r.flood.wadeBp : 10000;
  const heart = r.heart;
  return heart && s.drink?.heart?.at === 'carried' && s.drink.heart.pid === h.pid ? mulBp(bp, heart.carryBp) : bp;
};

/** The point `d` along the water's way. */
const pointAt = (s: BattleState, d: number): { x: number; y: number } | null => {
  const pts = chain(s);
  let run = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1] as { x: number; y: number };
    const b = pts[i] as { x: number; y: number };
    const len = imax(1, isqrt(dist2(a.x, a.y, b.x, b.y)));
    if (run + len >= d) {
      const t = d - run;
      return { x: a.x + idiv((b.x - a.x) * t, len), y: a.y + idiv((b.y - a.y) * t, len) };
    }
    run += len;
  }
  return pts[pts.length - 1] ?? null;
};
